/**
 * The practice questions the waitlist pages ask after the name and email, so
 * the team knows who is booking before the call. The answers go with the
 * signup to its row in the Sheet (joinWaitlist in src/lib/waitlist.ts) and
 * into the booking form's notes.
 */
import type { WaitlistAnswers } from "@/lib/waitlist";

/** The choice that asks to type the answer instead. */
export const OTHER = "Other";

export type Question =
  | {
      kind: "choice";
      name: "role" | "providers";
      legend: string;
      options: readonly string[];
      /** The field picking Other adds, whose text replaces it. */
      other?: { name: "role_other"; label: string };
    }
  | { kind: "text"; name: "current_ehr"; legend: string; placeholder: string };

/** A field the questions form needs filled, in the form's order. */
export type AnswerField = Question["name"] | "role_other";

const QUESTIONS: readonly Question[] = [
  {
    kind: "choice",
    name: "role",
    legend: "Your role",
    options: ["Owner", "Clinician", "Practice manager", "Billing", OTHER],
    other: { name: "role_other", label: "Your role" },
  },
  { kind: "choice", name: "providers", legend: "Providers in the practice", options: ["Just me", "2–5", "6–20", "21+"] },
  { kind: "text", name: "current_ehr", legend: "Which EHR do you use today?", placeholder: "Your current EHR, or none" },
];

/** /ehr asks for the current EHR; /rate-check does not. */
export function questionsFor(askCurrentEhr: boolean): readonly Question[] {
  return QUESTIONS.filter((q) => askCurrentEhr || q.name !== "current_ehr");
}

export const ANSWER_MAX_LENGTH = 120;

/** The answers in the questions form, or the first field left empty. */
export function readAnswers(
  data: FormData,
  askCurrentEhr: boolean,
): { answers: WaitlistAnswers; missing: null } | { answers: null; missing: AnswerField } {
  const read = (name: string) => String(data.get(name) ?? "").trim().slice(0, ANSWER_MAX_LENGTH);
  const answers: Partial<Record<Question["name"], string>> = {};
  for (const q of questionsFor(askCurrentEhr)) {
    let value = read(q.name);
    if (!value) return { answers: null, missing: q.name };
    if (q.kind === "choice" && q.other && value === OTHER) {
      value = read(q.other.name);
      if (!value) return { answers: null, missing: q.other.name };
    }
    answers[q.name] = value;
  }
  return { answers: answers as WaitlistAnswers, missing: null };
}

/** The answers as lines of the booking form's notes. */
export function answersNote(answers: WaitlistAnswers): string {
  return [
    `Role: ${answers.role}`,
    `Providers: ${answers.providers}`,
    ...(answers.current_ehr ? [`Current EHR: ${answers.current_ehr}`] : []),
  ].join("\n");
}
